import { useState } from "react";
import { CartesianGrid, LabelList, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartSeriesConfig, ReferenceLineConfig } from "../types";
import { useLegendToggle } from "./useLegendToggle";

// Tooltip opcional: formata o valor (ex.: média a 2 casas, % a 1 casa) e pode mostrar uma contagem
// por ponto lida de `point[seriesKey + countSuffixKey]` (ex.: nº de questionários por trimestre).
export type TimeSeriesTooltipConfig = {
    valueFormatter?: (value: number) => string;
    countSuffixKey?: string;
    countLabel?: string;
    // Se definido, mostra uma fração `point[key+countNumeratorSuffixKey]/point[key+countSuffixKey]`
    // (ex.: desistências/total) em vez de só a contagem.
    countNumeratorSuffixKey?: string;
};

type TimeSeriesChartProps = {
    points: Array<Record<string, string | number>>;
    series: Array<ChartSeriesConfig>;
    height?: number;
    showPointLabels?: boolean;
    referenceLine?: ReferenceLineConfig;
    tooltip?: TimeSeriesTooltipConfig;
    yDomain?: [number | string | ((value: number) => number), number | string | ((value: number) => number)];
    yTicks?: Array<number>;
};

type TooltipEntry = { dataKey?: string | number; name?: string; value?: number | string | null; color?: string; payload?: Record<string, any> };

const SeriesTooltipContent = ({
    active,
    payload,
    label,
    config,
    activeKey,
}: {
    active?: boolean;
    payload?: Array<TooltipEntry>;
    label?: string | number;
    config: TimeSeriesTooltipConfig;
    activeKey?: string | null;
}) => {
    if (!active || !payload || payload.length === 0) return null;
    const point = payload[0].payload || {};
    const visibleRows = payload.filter((entry) => entry.value !== null && entry.value !== undefined);
    if (visibleRows.length === 0) return null;

    // Quando o rato está sobre uma linha específica, essa fica em destaque (a 1ª, a negrito) e as outras esbatidas.
    const highlightedKey = activeKey && visibleRows.some((entry) => String(entry.dataKey) === activeKey) ? activeKey : null;
    const orderedRows = highlightedKey
        ? [...visibleRows].sort(
              (firstEntry, secondEntry) =>
                  (String(secondEntry.dataKey) === highlightedKey ? 1 : 0) - (String(firstEntry.dataKey) === highlightedKey ? 1 : 0)
          )
        : visibleRows;

    const rows = orderedRows.map((entry) => {
        const isHighlighted = highlightedKey === String(entry.dataKey);
        const isDimmed = highlightedKey !== null && !isHighlighted;
        const formattedValue = config.valueFormatter ? config.valueFormatter(Number(entry.value)) : String(entry.value);
        const countKey = config.countSuffixKey ? `${entry.dataKey}${config.countSuffixKey}` : null;
        const count = countKey ? point[countKey] : undefined;
        const numerator = config.countNumeratorSuffixKey ? point[`${entry.dataKey}${config.countNumeratorSuffixKey}`] : undefined;
        const hasCount = count !== undefined && count !== null;
        const countText = !hasCount
            ? ""
            : numerator !== undefined && numerator !== null
              ? ` (${numerator}/${count})`
              : ` (${count}${config.countLabel ? ` ${config.countLabel}` : ""})`;
        return (
            <div
                key={String(entry.dataKey)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: isHighlighted ? 13 : 12,
                    fontWeight: isHighlighted ? 700 : 400,
                    color: isDimmed ? "#9e9e9e" : entry.color,
                    opacity: isDimmed ? 0.7 : 1,
                }}
            >
                <span style={{ width: isHighlighted ? 9 : 7, height: isHighlighted ? 9 : 7, borderRadius: "50%", background: entry.color, flexShrink: 0 }} />
                <span>
                    {entry.name}: {formattedValue}
                    {countText}
                </span>
            </div>
        );
    });

    const content = (
        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 6, padding: "6px 10px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{label}</div>
            {rows}
        </div>
    );
    return content;
};

export const TimeSeriesChart = ({
    points,
    series,
    height = 380,
    showPointLabels = true,
    referenceLine,
    tooltip,
    yDomain,
    yTicks,
}: TimeSeriesChartProps) => {
    const { hiddenKeys, legendProps } = useLegendToggle();
    const [activeSeriesKey, setActiveSeriesKey] = useState<string | null>(null);
    const hasRightAxis = series.some((serie) => serie.rightAxis);

    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart data={points} margin={{ top: 24, right: 24, bottom: 8, left: 0 }} onMouseLeave={() => setActiveSeriesKey(null)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="monthLabel" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" domain={yDomain} ticks={yTicks} allowDataOverflow={Boolean(yDomain)} allowDecimals={yDomain ? false : undefined} tick={{ fontSize: 12 }} />
                {hasRightAxis && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />}
                {referenceLine && (
                    <ReferenceLine
                        yAxisId="left"
                        y={referenceLine.value}
                        stroke={referenceLine.color || "#E25555"}
                        strokeDasharray="4 4"
                        label={referenceLine.label ? { value: referenceLine.label, position: "right", fontSize: 10, fill: referenceLine.color || "#E25555" } : undefined}
                    />
                )}
                {tooltip ? <Tooltip content={<SeriesTooltipContent config={tooltip} activeKey={activeSeriesKey} />} /> : <Tooltip />}
                <Legend {...legendProps} />
                {series.map((serie) => (
                    <Line
                        key={serie.key}
                        yAxisId={serie.rightAxis ? "right" : "left"}
                        type="monotone"
                        dataKey={serie.key}
                        name={serie.label}
                        stroke={serie.color}
                        strokeWidth={serie.strokeWidth ?? 2}
                        dot={{ r: (serie.strokeWidth ?? 2) > 2 ? 4 : 3 }}
                        activeDot={{ r: (serie.strokeWidth ?? 2) > 2 ? 7 : 6, onMouseEnter: () => setActiveSeriesKey(serie.key) }}
                        hide={hiddenKeys.has(serie.key)}
                        connectNulls={false}
                        isAnimationActive={false}
                        onMouseEnter={() => setActiveSeriesKey(serie.key)}
                        onMouseMove={() => setActiveSeriesKey(serie.key)}
                    >
                        {showPointLabels && <LabelList dataKey={serie.key} position="top" fontSize={10} fill={serie.color} />}
                    </Line>
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
};
